import React, { useState } from 'react';
import { Loader2Icon } from 'lucide-react';
interface ResumeSignInProps {
  planName: string | null;
  submitting: boolean;
  error: string | null;
  onSubmit: (email: string, password: string) => void;
  onCancel: () => void;
}
const inputStyles = 'mt-2 block w-full rounded-xl border border-charcoal/20 bg-white px-4 py-3 text-base text-charcoal focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-purple-600';

/**
 * Shown on the membership step when there's no in-memory session (page refresh,
 * or returning from a cancelled Stripe Checkout). Signs in with Supabase Auth,
 * then continues to checkout for the selected plan.
 */
export function ResumeSignIn({
  planName,
  submitting,
  error,
  onSubmit,
  onCancel
}: ResumeSignInProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!email.trim() || !password) return;
    onSubmit(email.trim().toLowerCase(), password);
  };
  return <section aria-labelledby="resume-heading" className="rounded-3xl border border-purple-200 bg-white px-6 py-7 md:px-8 shadow-[0_18px_50px_-30px_rgba(44,44,44,0.5)]" data-id="element-504" data-mp="1ukkcwa0tstvt">
      <h2 id="resume-heading" className="font-serif text-2xl text-charcoal" data-id="element-505" data-mp="24bym59ikx825">
        Sign in to continue
      </h2>
      <p className="mt-2 text-base text-charcoal/70 leading-relaxed" data-id="element-506" data-mp="1zfzqd0ux8nrp">
        Use the email and password you just created
        {planName ? ` and we'll take you to secure checkout for ${planName}.` : '.'}
      </p>

      <form id="practitioner-resume-form" noValidate onSubmit={handleSubmit} className="mt-6 grid gap-4 sm:grid-cols-2" data-id="element-507" data-mp="1llyzsue4i72f">
        <div data-id="element-508" data-mp="1gmw0lqktqv2d">
          <label htmlFor="resume_email" className="text-sm font-medium text-charcoal" data-id="element-509" data-mp="1gmcm6juduot3">
            Email
          </label>
          <input id="resume_email" name="email" type="email" autoComplete="email" required value={email} onChange={e => setEmail(e.target.value)} className={inputStyles} data-id="element-510" data-mp="2wzko6dc5s4mw" />
        </div>
        <div data-id="element-511" data-mp="342r4mwjrcasm">
          <label htmlFor="resume_password" className="text-sm font-medium text-charcoal" data-id="element-512" data-mp="346ondh2fb0bz">
            Password
          </label>
          <input id="resume_password" name="password" type="password" autoComplete="current-password" required value={password} onChange={e => setPassword(e.target.value)} className={inputStyles} data-id="element-513" data-mp="3e2i2n87j17xg" />
        </div>

        {error && <p role="alert" className="sm:col-span-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" data-id="element-514" data-mp="2qbmcefipyksb">
            {error}
          </p>}

        <div className="sm:col-span-2 flex flex-wrap items-center gap-4" data-id="element-515" data-mp="1xpbdd9argrxm">
          <button type="submit" disabled={submitting || !email.trim() || !password} className="inline-flex items-center justify-center gap-2 rounded-full bg-purple-700 px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-200 ease-out hover:bg-purple-800 disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-600 focus-visible:ring-offset-2" data-id="element-516" data-mp="22nmjb8xxqiak">
            {submitting && <Loader2Icon aria-hidden="true" className="w-4 h-4 animate-spin" data-id="element-517" data-mp="tma4bipak8j" />}
            {submitting ? 'Signing in…' : 'Sign in and continue'}
          </button>
          <button type="button" onClick={onCancel} className="text-sm font-medium text-charcoal/65 hover:text-purple-800 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-600" data-id="element-518" data-mp="3s6qt0jtyn599">
            Cancel
          </button>
        </div>
      </form>
    </section>;
}